import { Formik } from "formik";
import React from "react";
import { Form } from "react-bootstrap";
import { Button } from '@blueprint/ui';
import * as yup from "yup";

import { AuthCodeField, FormFooter } from "../../../common/components/form";
import { FormSubmissionHandler } from "../../../types/common";
import { PageTitle } from "../../../types/pageTitle";

import PublicFormContainer from "./PublicFormContainer";

export type ValidateOtpFormValues = {
    otp: string;
};

const schema: yup.SchemaOf<ValidateOtpFormValues> = yup.object().shape({
    otp: yup
        .string()
        .required()
        .matches(
            /^[0-9]{6}$/,
            "You didn't enter the expected verification code. Please try again.",
        ),
});

type ValidateOtpFormProps = {
    onSubmit: FormSubmissionHandler<ValidateOtpFormValues>;
    onBack: () => void;
    forgotOTP: () => void;
};

const ValidateOtpForm = ({
    onSubmit,
    onBack,
    forgotOTP,
}: ValidateOtpFormProps): JSX.Element => {
    document.title = PageTitle.ValidateOtpForm;
    return (
        <PublicFormContainer>
            <Formik
                validationSchema={schema}
                validateOnBlur={false}
                validateOnChange={false}
                onSubmit={onSubmit}
                initialValues={{
                    otp: "",
                }}
            >
                {({ handleSubmit, errors, isSubmitting }) => (
                    <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                        <h2 className="mb-4">
                            2 Factor Authentication
                        </h2>
                        <p className="mb-5" id="optValidatePolicy">
                            Please enter the code displayed on your
                            authenticator app from your device.
                        </p>
                        <AuthCodeField name="otp" label="Code" autoFocus ariaLabelledBy="optValidatePolicy" required={true}/>
                        {errors.otp && (
                            <Button
                                as="a"
                                className="border-0 px-0 mt-3 text-decoration-underline text-reset"
                                variant="link"
                                href="https://support.microsoft.com/en-us/account-billing/common-problems-with-two-step-verification-for-a-work-or-school-account-63acbb9b-16a1-47b9-8619-6a865e8071a5"
                                target="_blank"
                            >
                                More information
                            </Button>
                        )}
                        <Button
                            as="a"
                            className="border-0 px-0 mt-3 text-decoration-underline text-reset"
                            disabled={isSubmitting}
                            onClick={forgotOTP}
                            variant="link">Click to reset 2 Factor Authentication and regenerate QR code</Button>

                        <FormFooter className="d-flex mb-5">
                            <Button
                                disabled={isSubmitting}
                                onClick={onBack}
                                variant="light.tertiary">Back</Button>
                            <Button
                                type="submit"
                                variant="light.primary"
                                style={{marginLeft: '10px'}}
                                disabled={isSubmitting}>Verify</Button>
                        </FormFooter>
                    </Form>
                )}
            </Formik>
        </PublicFormContainer>
    );
};

export default ValidateOtpForm;
